import { useState, useEffect, useRef, useCallback } from 'react'
import { Route, useRouteMatch, useHistory, useLocation } from 'react-router-dom'
import styled, { keyframes, css } from 'styled-components'
import { Flex, lightColors } from 'vvs-uikit'
import historyBackOrReplace from 'utils/historyBackOrReplace'

import { FullScreenStackContentFlex } from '../styles'

const OPENED_TOP = '0%'

/**
 * A stack page with slide up/down transition, and make it like `<Route />` from react-router-dom
 *
 * to make this appear like popup,
 *   `import { StackContentFlex } from '../styles'`
 * and pass as props like
 *   `<ScreenStackRoute path={...} ContentFlex={StackContentFlex} />`
 */
const ScreenStackRoute: React.FC<{
  path: string | string[]
  transition?: number // in ms
  ContentFlex?: React.FC<any>
  backPath?: string | [path: string, state: unknown]
}> = ({ path, children, ContentFlex = FullScreenStackContentFlex, transition = 500, backPath }) => {
  const history = useHistory()
  const match = useRouteMatch(path)
  const location = useLocation()
  const isMatch = !!match
  const [isRendering, setIsRendering] = useState(isMatch)
  const [top, setTop] = useState(isMatch ? '0%' : null)
  const [internalLocation, setInternalLocation] = useState(location)

  const transitionRef = useRef<number>()
  transitionRef.current = transition

  const stackFlex = useRef<HTMLDivElement>()
  const contentFlex = useRef<HTMLDivElement>()
  const setTopToSlideDown = useCallback(() => {
    setTop(`${(contentFlex.current.clientHeight / stackFlex.current.clientHeight) * 100}%`)
  }, [])

  useEffect(() => {
    if (isMatch) {
      setIsRendering(true)
      let handle = requestAnimationFrame(() => {
        setTopToSlideDown()
        handle = requestAnimationFrame(() => {
          setTop(OPENED_TOP)
        })
      })
      return () => cancelAnimationFrame(handle)
    }

    if (contentFlex.current && stackFlex.current) {
      setTopToSlideDown()
      const handle = setTimeout(() => {
        setIsRendering(false)
        setTop(null)
      }, transitionRef.current)
      return () => clearTimeout(handle)
    }
    return () => undefined
  }, [isMatch, setTopToSlideDown])

  useEffect(() => {
    if (isMatch) {
      setInternalLocation(location)
    }
  }, [location, isMatch])

  if (!isRendering) return null
  return (
    <ScreenStackFlex
      ref={stackFlex}
      top={top}
      transition={transition}
      onClick={(event: React.MouseEvent) => {
        if (event.target === stackFlex.current || event.target === contentFlex.current) {
          if (backPath) {
            const [backPathString, backState] = typeof backPath === 'string' ? [backPath] : backPath
            historyBackOrReplace(history, backPathString, backState)
          } else {
            history.goBack()
          }
        }
      }}
    >
      <Route path={path} location={internalLocation}>
        <ContentFlex ref={contentFlex}>{children}</ContentFlex>
      </Route>
    </ScreenStackFlex>
  )
}

export default ScreenStackRoute

const backgroundFadeIn = keyframes`
  from {
    background-color: ${lightColors.darkestNightBlue}00;
  }
  to {
    background-color: ${lightColors.darkestNightBlue}d9;
  }
`

const ScreenStackFlex = styled(Flex)<{
  top: string
  transition: number
}>`
  position: absolute;
  left: 0%;
  height: 100%;
  width: 100%;

  justify-content: end;
  flex-direction: column;

  transition: top ${({ transition }) => transition}ms;
  ${({ top }) => top && `top: ${top};`}

  ${({ top, transition }) =>
    top === OPENED_TOP &&
    css`
      animation: 100ms ease ${transition}ms 1 normal both running ${backgroundFadeIn};
    `}
`
